const API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";
const LANGUAGES = [
  { id: "pt", label: "Português" },
  { id: "en", label: "English" },
  { id: "es", label: "Español" },
];

function FlagIcon({ code, size }) {
  const w = size || 18;
  const h = Math.round(w * 0.72);
  const clipId = React.useId().replace(/:/g, "") + "-" + code;
  return (
    <svg viewBox="0 0 20 14" width={w} height={h} style={{ borderRadius: 3, flexShrink: 0, display: "block" }}>
      <defs>
        <clipPath id={clipId}><rect width="20" height="14" rx="2" /></clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {code === "pt" && (
          <React.Fragment>
            <rect width="20" height="14" fill="#009739" />
            <polygon points="10,2 18,7 10,12 2,7" fill="#FEDD00" />
            <circle cx="10" cy="7" r="3.2" fill="#012169" />
          </React.Fragment>
        )}
        {code === "en" && (
          <React.Fragment>
            <rect width="20" height="14" fill="#B22234" />
            <rect y="1.08" width="20" height="1.08" fill="#fff" />
            <rect y="3.23" width="20" height="1.08" fill="#fff" />
            <rect y="5.38" width="20" height="1.08" fill="#fff" />
            <rect y="7.54" width="20" height="1.08" fill="#fff" />
            <rect y="9.69" width="20" height="1.08" fill="#fff" />
            <rect y="11.85" width="20" height="1.08" fill="#fff" />
            <rect width="9" height="7.5" fill="#3C3B6E" />
          </React.Fragment>
        )}
        {code === "es" && (
          <React.Fragment>
            <rect width="20" height="14" fill="#AA151B" />
            <rect y="3.5" width="20" height="7" fill="#F1BF00" />
          </React.Fragment>
        )}
      </g>
    </svg>
  );
}

const AD_TABS = ["noticias", "rota"];
const AD_THROTTLE_MS = 2 * 60 * 1000;

function trackEvent(event) {
  try {
    const deviceId = window.getDeviceId ? window.getDeviceId() : null;
    if (!deviceId) return;
    fetch(`${API_BASE}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId, event }),
    }).catch(() => {});
  } catch (e) {}
}
window.trackEvent = trackEvent;

function generateStrongDeviceId() {
  // deviceId is the only credential this app checks for a wide surface of
  // endpoints (profile, places, friends, notifications, communities, and
  // even setting a first password on an account) — the old
  // Date.now()+Math.random() scheme is guessable/brute-forceable, so new
  // devices get a real random ID instead. Existing installs keep whatever
  // they already generated (read from localStorage below), since changing
  // it retroactively would just log everyone out.
  if (window.crypto && window.crypto.randomUUID) {
    return "d" + window.crypto.randomUUID();
  }
  if (window.crypto && window.crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return "d" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return "d" + Date.now() + Math.random().toString(36).slice(2);
}
function getDeviceId() {
  try {
    let id = localStorage.getItem("vigial_device_id");
    if (!id) {
      id = generateStrongDeviceId();
      localStorage.setItem("vigial_device_id", id);
    }
    return id;
  } catch (e) { return "anon"; }
}
window.getDeviceId = getDeviceId;
window.generateStrongDeviceId = generateStrongDeviceId;

// Google Play policy requires digital subscriptions purchased from inside
// an Android app to go through Google Play's own billing system. We charge
// via Mercado Pago instead, so the Android (TWA) build must not offer any
// way to buy Premium in-app — only the plain web/PWA can. Chrome sets this
// referrer specifically when a page is launched from a Trusted Web
// Activity, which is the standard way to detect "running as the Android
// app" from web code.
function isTWA() {
  try {
    return document.referrer.startsWith("android-app://");
  } catch (e) { return false; }
}
window.isTWA = isTWA;

function getBrowserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation || (typeof window.isSecureContext !== "undefined" && !window.isSecureContext)) { resolve(null); return; }
    const timer = setTimeout(() => resolve(null), 600);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { clearTimeout(timer); resolve(null); },
      { timeout: 600, maximumAge: 60000 }
    );
  });
}

function defaultDraft() {
  return { category: null, lat: -9.605, lng: -35.745, desc: "", occurredAt: null };
}

function pushAccountSync(partial) {
  const deviceId = getDeviceId();
  fetch(`${API_BASE}/account-sync`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId, ...partial }),
  }).catch(() => {});
}
window.pushAccountSync = pushAccountSync;

function mergePlacesById(a, b) {
  const byId = {};
  [...(a || []), ...(b || [])].forEach((p) => { if (p && p.id) byId[p.id] = p; });
  return Object.values(byId);
}

function pullAccountSync(deviceId) {
  fetch(`${API_BASE}/account-sync/mine`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
  })
    .then((r) => r.json())
    .then((data) => {
      if (!data || !data.linked) return;
      let changed = false;
      if (data.premium && !(window.isPremium && window.isPremium())) {
        window.setPremium && window.setPremium(true);
        changed = true;
      }
      if (Array.isArray(data.places)) {
        let local = [];
        try { local = JSON.parse(localStorage.getItem(window.PLACES_KEY || "vigial_saved_places_v1") || "[]"); } catch (e) {}
        const merged = mergePlacesById(local, data.places);
        if (merged.length !== local.length) {
          try { localStorage.setItem(window.PLACES_KEY || "vigial_saved_places_v1", JSON.stringify(merged)); } catch (e) {}
          changed = true;
        }
      }
      if (Array.isArray(data.myBairros)) {
        let local = [];
        try { local = JSON.parse(localStorage.getItem("vigial_my_bairros_v1") || "[]"); } catch (e) {}
        const merged = Array.from(new Set([...local, ...data.myBairros]));
        if (merged.length !== local.length) {
          try { localStorage.setItem("vigial_my_bairros_v1", JSON.stringify(merged)); } catch (e) {}
          changed = true;
        }
      }
      if (data.profile) {
        let localProfile = null;
        try { localProfile = JSON.parse(localStorage.getItem("vigial_user_profile_v1") || "null"); } catch (e) {}
        if (JSON.stringify(localProfile) !== JSON.stringify(data.profile)) {
          try { localStorage.setItem("vigial_user_profile_v1", JSON.stringify(data.profile)); } catch (e) {}
          changed = true;
        }
      }
      if (changed) window.location.reload();
    })
    .catch(() => {});
}

function App() {
  const { TabBar, Icon } = window.VigiALDesignSystem_ff2dec;
  const [tab, setTab] = React.useState("mapa");
  const [lang, setLangState] = React.useState(window.getLang());
  const [filter, setFilter] = React.useState("24h");
  const [reports, setReports] = React.useState([]);
  const [showModal, setShowModal] = React.useState(false);
  const [draft, setDraft] = React.useState(defaultDraft());
  const [toast, setToast] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerProfile, setDrawerProfile] = React.useState(null);
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const [profileCardOpen, setProfileCardOpen] = React.useState(false);
  const [adTab, setAdTab] = React.useState(null);
  const [pendingArticleLink, setPendingArticleLink] = React.useState(null);
  const deviceId = React.useMemo(getDeviceId, []);
  const [myTrustLevel, setMyTrustLevel] = React.useState(0);
  // Resets to true on every fresh app load (not persisted) — the point is
  // to catch people who don't realize signup exists (reported by an older
  // relative of a tester) each time they open the app, not just once ever,
  // while staying quiet for the rest of that session once seen or acted on.
  const [showSignupBanner, setShowSignupBanner] = React.useState(true);
  const [headerProfile, setHeaderProfile] = React.useState(() => (window.loadProfile ? window.loadProfile() : null));
  const [unreadNotifCount, setUnreadNotifCount] = React.useState(0);
  const [comunidadesDeepLink, setComunidadesDeepLink] = React.useState(null);
  const profileCardRef = React.useRef(null);

  function refreshUnreadNotifCount() {
    fetch(`${API_BASE}/notifications/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    })
      .then((r) => r.json())
      .then((d) => { if (d && d.linked) setUnreadNotifCount(d.unreadCount || 0); })
      .catch(() => {});
  }

  React.useEffect(() => {
    if (!profileCardOpen) return undefined;
    function handleClickOutside(e) {
      if (profileCardRef.current && !profileCardRef.current.contains(e.target)) {
        setProfileCardOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileCardOpen]);

  function refreshMyTrustLevel() {
    fetch(`${API_BASE}/device-trust/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    }).then((r) => r.json()).then((d) => setMyTrustLevel(d.trustLevel || 0)).catch(() => {});
  }

  // window.changeTab is assigned once below (deps=[deviceId], never re-runs),
  // so a direct `window.changeTab = changeTab` would freeze on the mount-time
  // closure forever — including the myTrustLevel it reads via maybeShowAd.
  // Routing through a ref that's updated every render keeps it calling the
  // current changeTab instead of a stale one.
  const changeTabRef = React.useRef(changeTab);
  changeTabRef.current = changeTab;

  React.useEffect(() => {
    refreshMyTrustLevel();
    pullAccountSync(deviceId);
    refreshUnreadNotifCount();
    trackEvent("app_open");
    trackEvent("tab_" + tab);
    window.refreshMyTrustLevel = refreshMyTrustLevel;
    window.changeTab = (id) => changeTabRef.current(id);
    // openNewsArticle only closes over the setPendingArticleLink setter
    // (stable across renders) and fresh window/localStorage reads, so unlike
    // changeTab it needs no ref indirection to stay current.
    window.vigiOpenNewsArticle = openNewsArticle;
    // Notifications otherwise only refresh on app load/reload — poll so a
    // friend request that arrives mid-session shows up on the bell badge
    // without the user having to reopen the app.
    const notifPoll = setInterval(refreshUnreadNotifCount, 60000);
    return () => { clearInterval(notifPoll); delete window.refreshMyTrustLevel; delete window.changeTab; delete window.vigiOpenNewsArticle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  React.useEffect(() => {
    function handleProfileChanged() {
      setHeaderProfile(window.loadProfile ? window.loadProfile() : null);
    }
    window.addEventListener("vigial:profileChanged", handleProfileChanged);
    return () => window.removeEventListener("vigial:profileChanged", handleProfileChanged);
  }, []);

  React.useEffect(() => {
    const splash = document.getElementById("app-splash");
    if (!splash) return undefined;
    const elapsed = Date.now() - (window.__vigiAlSplashStart || Date.now());
    const wait = Math.max(0, 700 - elapsed);
    const showTimer = setTimeout(() => {
      splash.classList.add("app-splash-hide");
      setTimeout(() => splash.remove(), 500);
    }, wait);
    return () => clearTimeout(showTimer);
  }, []);

  React.useEffect(() => {
    // Mercado Pago redirects back here with ?premium_return=1 after checkout
    // — the webhook that actually confirms payment can land a few seconds
    // after that redirect, so poll briefly instead of trusting the redirect
    // itself (it fires on cancel/back-button too, not just success).
    if (!window.location.search.includes("premium_return=1")) return;
    window.history.replaceState(null, "", window.location.pathname);
    changeTab("perfil");
    showToast(t(lang, "premiumConfirming"));
    let attempts = 0;
    const maxAttempts = 6;
    const poll = () => {
      attempts += 1;
      fetch(`${API_BASE}/account-sync/mine`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.linked && data.premium) {
            showToast(t(lang, "premiumConfirmed"));
            setTimeout(() => window.location.reload(), 1200);
            return;
          }
          if (attempts < maxAttempts) setTimeout(poll, 2000);
          else showToast(t(lang, "premiumStillProcessing"));
        })
        .catch(() => {
          if (attempts < maxAttempts) setTimeout(poll, 2000);
        });
    };
    poll();
  }, []);

  React.useEffect(() => {
    // A push notification's service worker click handler navigates here
    // with ?notif=<id> instead of just opening the app — look up that
    // notification and reuse the exact same routing navigateFromNotification
    // already does for a tap inside the in-app list, so a click "from
    // outside" lands on the actual community/friend request instead of
    // just the map.
    const notifId = new URLSearchParams(window.location.search).get("notif");
    if (!notifId) return;
    window.history.replaceState(null, "", window.location.pathname);
    fetch(`${API_BASE}/notifications/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    })
      .then((r) => r.json())
      .then((data) => {
        const n = data && Array.isArray(data.notifications) && data.notifications.find((x) => x.id === notifId);
        if (n) navigateFromNotification(n);
        else changeTab("notificacoes");
      })
      .catch(() => changeTab("notificacoes"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setHeaderProfile(window.loadProfile ? window.loadProfile() : null);
    setProfileCardOpen(false);
  }, [tab]);

  function changeLang(l) { window.setLang(l); setLangState(l); setLangMenuOpen(false); }
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 2400); }
  function openDrawer() {
    setDrawerProfile(window.loadProfile ? window.loadProfile() : null);
    setDrawerOpen(true);
  }

  function maybeShowAd(id) {
    if (!AD_TABS.includes(id)) return;
    if (window.isPremium && window.isPremium()) return;
    if (myTrustLevel >= 3) return;
    const key = "vigial_ad_last_" + id;
    const last = Number(localStorage.getItem(key) || 0);
    const now = Date.now();
    if (now - last < AD_THROTTLE_MS) return;
    localStorage.setItem(key, String(now));
    setAdTab(id);
  }

  function changeTab(id) {
    setTab(id);
    maybeShowAd(id);
    trackEvent("tab_" + id);
  }

  // Same "vigial_ad_last_noticias" throttle key NoticiasScreen.jsx already
  // uses for its own article-click ad, so opening a news link from the map,
  // Meu Bairro, or Alertas shares that one 2-minute budget instead of
  // stacking a separate ad quota on top of it.
  function openNewsArticle(link) {
    if (!link || !/^https?:\/\//i.test(link)) return;
    if (window.isPremium && window.isPremium()) { window.open(link, "_blank", "noopener,noreferrer"); return; }
    const key = "vigial_ad_last_noticias";
    const last = Number(localStorage.getItem(key) || 0);
    const now = Date.now();
    if (now - last < AD_THROTTLE_MS) { window.open(link, "_blank", "noopener,noreferrer"); return; }
    localStorage.setItem(key, String(now));
    setPendingArticleLink(link);
  }

  function navigateFromDrawer(id) {
    changeTab(id);
    setDrawerOpen(false);
  }

  function navigateFromNotification(n) {
    if (n.type === "friend_request" || n.type === "friend_accepted") {
      setComunidadesDeepLink({ tab: "amigos" });
    } else if (n.type === "community_join_request" && n.communityId) {
      setComunidadesDeepLink({ tab: "comunidades", communityId: n.communityId, detailTab: "membros" });
    } else if ((n.type === "community_joined" || n.type === "community_invited" || n.type === "community_join_accepted") && n.communityId) {
      setComunidadesDeepLink({ tab: "comunidades", communityId: n.communityId });
    } else if (n.type === "content_reported" && n.communityId) {
      setComunidadesDeepLink({ tab: "comunidades", communityId: n.communityId, detailTab: n.contentType === "chat" ? "chat" : "mural" });
    } else if (n.type === "community_new_post" && n.communityId) {
      setComunidadesDeepLink({ tab: "comunidades", communityId: n.communityId, detailTab: "mural" });
    } else {
      setComunidadesDeepLink(null);
    }
    changeTab("comunidades");
  }

  function loadReports() {
    fetch(`${API_BASE}/reports?window=${filter}`)
      .then((r) => r.json())
      .then((data) => setReports(data))
      .catch(() => showToast(t(lang, "cantReachServer")));
  }
  React.useEffect(loadReports, [filter]);

  function submitReport(overrideDraft) {
    const d = overrideDraft || draft;
    if (!d.category) { showToast(t(lang, "chooseCategory")); return; }
    fetch(`${API_BASE}/reports`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: d.category, lat: d.lat, lng: d.lng, desc: d.desc, occurredAt: d.occurredAt, deviceId }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.error) { showToast(res.error === "conta temporariamente suspensa de criar novos relatos devido a relatos marcados como falsos" ? t(lang, "reportSuspendedErr") : res.error); return; }
        setShowModal(false);
        setDraft(defaultDraft());
        showToast(t(lang, "reportSent"));
        loadReports();
        trackEvent("report_create");
      })
      .catch(() => showToast(t(lang, "couldNotSend")));
  }

  function voteReport(id, action) {
    getBrowserLocation().then((loc) => {
      fetch(`${API_BASE}/reports/${id}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lat: loc && loc.lat, lng: loc && loc.lng, deviceId }),
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.error) { showToast(res.error); return; }
          setReports((prev) => prev.map((r) => (r.id === id ? { ...r, confirms: res.confirms, score: res.score } : r)));
        })
        .catch(() => showToast(t(lang, "couldNotRegister")));
    });
  }
  function confirmReport(id) { voteReport(id, "confirm"); }
  function dislikeReport(id) { voteReport(id, "dislike"); }

  const visible = reports;

  return (
    <div className="vigi-app-card" style={{ position: "relative", width: 380, height: "min(640px, 92vh)", background: "var(--color-surface)", borderRadius: "var(--radius-2xl)", border: "1px solid var(--color-border)", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "var(--shadow-card)" }}>
      <div style={{ padding: "16px 18px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--color-border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={openDrawer} style={{ position: "relative", background: "transparent", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0 }}>
            <Icon name="menu" size={20} color="var(--color-text-secondary)" />
            {unreadNotifCount > 0 && (
              <span style={{
                position: "absolute", top: -3, right: -3, minWidth: 13, height: 13, borderRadius: 7,
                background: "var(--color-alert)", color: "#fff", fontSize: 8, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", lineHeight: 1,
                border: "1.5px solid var(--color-surface)",
              }}>
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </button>
          <img src="logo.png" width={20} height={20} alt="" style={{ borderRadius: 5, flexShrink: 0, display: "block" }} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "var(--text-lg)", whiteSpace: "nowrap" }}>Alerta AL</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <div ref={profileCardRef} style={{ position: "relative", flexShrink: 0 }}>
            <button onClick={() => setProfileCardOpen((v) => !v)} style={{
              display: "flex", alignItems: "center", gap: 4, padding: "2px 6px 2px 2px", borderRadius: "var(--radius-pill)",
              border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", minWidth: 0, maxWidth: 84,
            }}>
              {window.AvatarBadge && <window.AvatarBadge avatar={headerProfile && headerProfile.avatar} size={18} />}
              <span style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {headerProfile ? (headerProfile.username || headerProfile.name) : t(lang, "menuGuest")}
              </span>
              {window.TRUST_LEVEL_STYLE && (
                <Icon
                  name={window.TRUST_LEVEL_STYLE[Math.min(myTrustLevel, 3)].icon}
                  size={11}
                  color={window.TRUST_LEVEL_STYLE[Math.min(myTrustLevel, 3)].color}
                  style={{ flexShrink: 0 }}
                />
              )}
            </button>
            {profileCardOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, width: 200,
                background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)",
                boxShadow: "var(--shadow-modal)", zIndex: 60, padding: 14,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  {window.AvatarBadge && <window.AvatarBadge avatar={headerProfile && headerProfile.avatar} size={44} />}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {headerProfile ? (headerProfile.username || headerProfile.name) : t(lang, "menuGuest")}
                    </div>
                    {headerProfile && headerProfile.username && headerProfile.name && (
                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {headerProfile.name}
                      </div>
                    )}
                  </div>
                </div>
                {window.TRUST_LEVEL_STYLE && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 8, padding: "3px 9px 3px 7px",
                    borderRadius: "var(--radius-pill)", background: "var(--color-brand-tint)",
                  }}>
                    <Icon
                      name={window.TRUST_LEVEL_STYLE[Math.min(myTrustLevel, 3)].icon}
                      size={12}
                      color={window.TRUST_LEVEL_STYLE[Math.min(myTrustLevel, 3)].color}
                    />
                    <span style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--color-brand-deep)" }}>
                      {t(lang, myTrustLevel >= 3 ? "trustLevel3" : myTrustLevel >= 2 ? "trustLevel2" : myTrustLevel >= 1 ? "trustLevel1" : "trustLevel0")}
                    </span>
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <span style={{
                    fontSize: "var(--text-xs)", fontWeight: 600, padding: "3px 9px", borderRadius: "var(--radius-pill)",
                    background: (window.isPremium && window.isPremium()) ? "var(--color-brand-primary)" : "var(--color-border)",
                    color: (window.isPremium && window.isPremium()) ? "#fff" : "var(--color-text-secondary)",
                  }}>
                    {t(lang, (window.isPremium && window.isPremium()) ? "planPremium" : "planFree")}
                  </span>
                </div>
                <button onClick={() => changeTab("perfil")} style={{
                  width: "100%", padding: "7px 0", borderRadius: 8, border: "1px solid var(--color-border)",
                  background: "transparent", color: "var(--color-brand-primary)", fontSize: "var(--text-xs)", fontWeight: 600,
                  cursor: "pointer", fontFamily: "var(--font-body)",
                }}>
                  {t(lang, "viewFullProfileBtn")}
                </button>
              </div>
            )}
          </div>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <button onClick={() => setLangMenuOpen((v) => !v)} style={{
              display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: "var(--radius-pill)",
              border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontFamily: "var(--font-mono)",
            }}>
              <FlagIcon code={lang} size={15} />
              <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>{lang.toUpperCase()}</span>
              <Icon name="chevron-down" size={11} color="var(--color-text-muted)" />
            </button>
            {langMenuOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: 140, maxHeight: 160, overflowY: "auto",
                background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 9,
                boxShadow: "var(--shadow-modal)", zIndex: 60,
              }}>
                {LANGUAGES.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => changeLang(l.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", boxSizing: "border-box",
                      background: lang === l.id ? "var(--color-brand-tint)" : "transparent", border: "none", cursor: "pointer",
                      fontFamily: "var(--font-body)", fontSize: "var(--text-sm)",
                      color: lang === l.id ? "var(--color-brand-deep)" : "var(--color-text-primary)",
                    }}
                  >
                    <FlagIcon code={l.id} size={18} />
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {tab === "mapa" && <window.MapaScreen lang={lang} filter={filter} setFilter={setFilter} reports={visible} onConfirm={confirmReport} onDislike={dislikeReport} onOpenReport={() => { setDraft(defaultDraft()); setShowModal(true); }} showToast={showToast} myTrustLevel={myTrustLevel} showSignupBanner={showSignupBanner && myTrustLevel < 1} onDismissSignupBanner={() => setShowSignupBanner(false)} />}
        {tab === "noticias" && <window.NoticiasScreen lang={lang} />}
        {tab === "emergencia" && <window.EmergenciaScreen lang={lang} expanded={expanded} setExpanded={setExpanded} />}
        {tab === "rota" && <window.RotaScreen lang={lang} showToast={showToast} />}
        {tab === "alertas" && <window.AlertasScreen lang={lang} />}
        {tab === "meubairro" && <window.MeuBairroScreen lang={lang} />}
        {tab === "comunidades" && <window.ComunidadesScreen lang={lang} deepLink={comunidadesDeepLink} />}
        {tab === "notificacoes" && <window.NotificacoesScreen lang={lang} onUnreadChange={setUnreadNotifCount} onNotificationClick={navigateFromNotification} />}
        {tab === "perfil" && <window.ProfileScreen lang={lang} />}
        {tab === "ajuda" && <window.AjudaScreen lang={lang} />}
      </div>

      <TabBar
        items={[
          { id: "mapa", label: t(lang, "tabMapa"), icon: (p) => <Icon name="map-pin" {...p} /> },
          { id: "rota", label: t(lang, "tabRota"), icon: (p) => <Icon name="route" {...p} /> },
          { id: "noticias", label: t(lang, "tabNoticias"), icon: (p) => <Icon name="newspaper" {...p} /> },
          { id: "emergencia", label: t(lang, "tabEmergencia"), icon: (p) => <Icon name="phone" {...p} /> },
        ]}
        active={tab} onChange={changeTab}
      />

      <window.SideDrawer lang={lang} open={drawerOpen} onClose={() => setDrawerOpen(false)} tab={tab} onNavigate={navigateFromDrawer} profile={drawerProfile} unreadNotifCount={unreadNotifCount} />

      {showModal && <window.ReportModal lang={lang} draft={draft} setDraft={setDraft} onClose={() => setShowModal(false)} onSubmit={submitReport} showToast={showToast} />}
      {adTab && <window.InterstitialAd lang={lang} onClose={() => setAdTab(null)} />}
      {pendingArticleLink && (
        <window.InterstitialAd
          lang={lang}
          willOpenExternal
          onClose={() => { const link = pendingArticleLink; setPendingArticleLink(null); window.open(link, "_blank", "noopener,noreferrer"); }}
        />
      )}
      {toast && (
        <div style={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", background: "var(--color-brand-deep)", color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8, zIndex: 50 }}>
          <Icon name="shield-check" size={15} color="#fff" /> {toast}
        </div>
      )}
    </div>
  );
}
ReactDOM.createRoot(document.getElementById("root")).render(<App />);
